// Single issue table row with status dot, priority icon, relative time
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { TableRow, TableCell } from '@/components/ui/table'
import { AlertTriangle, AlertCircle, Info, Minus } from 'lucide-react'
import { formatRelativeTime } from '@/lib/format-time'
import { IssueQuickStatusChange } from './IssueQuickStatusChange'
import type { IssueRow } from '@/hooks/useIssues'

const STATUS_DOT: Record<string, string> = {
  draft: 'bg-gray-300',
  open: 'bg-blue-500',
  in_progress: 'bg-amber-500',
  waiting_info: 'bg-purple-500',
  resolved: 'bg-emerald-500',
  closed: 'bg-gray-400',
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  open: 'Open',
  in_progress: 'In Progress',
  waiting_info: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
}

const PRIORITY_ICON: Record<string, JSX.Element> = {
  critical: <AlertTriangle className="h-3.5 w-3.5 text-red-600" />,
  high: <AlertCircle className="h-3.5 w-3.5 text-amber-600" />,
  medium: <Info className="h-3.5 w-3.5 text-blue-500" />,
  low: <Minus className="h-3.5 w-3.5 text-gray-400" />,
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-amber-50 text-amber-700 border-amber-200',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  low: 'bg-gray-50 text-gray-600 border-gray-200',
}

interface IssuesTableRowProps {
  issue: IssueRow
}

export function IssuesTableRow({ issue }: IssuesTableRowProps): JSX.Element {
  const navigate = useNavigate()

  return (
    <TableRow
      className="cursor-pointer hover:bg-kaycoo-teal-pale/20"
      onClick={() => navigate(`/issues/${issue.id}`)}
    >
      <TableCell className="max-w-[280px]">
        <p className="truncate font-medium text-kaycoo-charcoal">{issue.title}</p>
      </TableCell>
      <TableCell className="text-kaycoo-charcoal-2">{issue.customer_name ?? '—'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[issue.status] ?? 'bg-gray-400'}`} />
          <span className="text-xs">{STATUS_LABEL[issue.status] ?? issue.status}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`gap-1 text-xs ${PRIORITY_COLORS[issue.priority] ?? ''}`}>
          {PRIORITY_ICON[issue.priority]}
          {issue.priority}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-kaycoo-charcoal-2">{issue.d365_version}</TableCell>
      <TableCell className="max-w-[100px] truncate text-xs text-kaycoo-charcoal-2">
        {issue.d365_module ?? '—'}
      </TableCell>
      <TableCell className="text-xs text-kaycoo-charcoal-2">
        {formatRelativeTime(issue.created_at)}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <IssueQuickStatusChange issueId={issue.id} currentStatus={issue.status} />
      </TableCell>
    </TableRow>
  )
}
